import type { ReactNode } from 'react'
import { useRef } from 'react'
import { Refine } from '@refinedev/core'
import * as Sentry from '@sentry/react'
import { Helmet, HelmetProvider } from 'react-helmet-async'

import '@/styles/global.css'
import '@/styles/metabase.css'
import '@/styles/hubspot.css'
import '@/styles/notaku.css'
import '@/styles/toastify.css'

import type { Metadata } from '@/types/metadata'
import { config } from '@/config'
import { applyDefaultSettings } from '@/lib/settings/apply-default-settings'
import { getSettings as getPersistedSettings } from '@/lib/settings/get-settings'
import { UserProvider } from '@/contexts/auth/user-context'
import { ImpersonationProvider } from '@/contexts/impersonation/impersonation-context'
import { SettingsProvider } from '@/contexts/settings'
import { dataProvider } from '@/providers/rest-data-provider'
import { createLiveProvider } from '@/providers/live-provider'
import { getPlatformSocket } from '@/lib/platform-socket'
import { Analytics } from '@/components/core/analytics'
import { SentryIdentify } from '@/components/core/sentry-identify'
import { SegmentIdentify } from '@/components/core/segment-identify'
import { SegmentSession } from '@/components/core/segment-session'
import { FullStory } from '@/components/core/fullstory'
import { HubSpotChat } from '@/components/core/hubspot'
import { ImpersonationBanner } from '@/components/core/impersonation-banner'
import { LocalizationProvider } from '@/components/core/localization-provider'
import { LogRocketProvider } from '@/components/core/logrocket'
import { ThemeProvider } from '@/components/core/theme-provider/theme-provider'
import { notificationProvider, Toaster } from '@/components/core/toaster'

import { BetterStackStatus } from './components/core/better-stack-status'
import { NotakuWidget } from './components/core/notaku'
import { resources } from './resources'

const metadata: Metadata = { title: config.site.name }

if (config.sentry.dsn) {
	Sentry.init({
		dsn: config.sentry.dsn,
		environment: import.meta.env.MODE,
		dataCollection: {
			// To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
			// https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#dataCollection
			// userInfo: false,
			// httpBodies: [],
		},
	})
}

interface RootProps {
	children: ReactNode
}

export function Root({ children }: RootProps) {
	const settings = useRef(applyDefaultSettings(getPersistedSettings()))
	const liveProvider = useRef(createLiveProvider(getPlatformSocket()))

	return (
		<Refine
			dataProvider={dataProvider(config.fridge.apiUrl!)}
			liveProvider={liveProvider.current}
			notificationProvider={notificationProvider}
			resources={resources}
			options={{ disableTelemetry: true }}
		>
			<HelmetProvider>
				<Helmet>
					<meta content={config.site.themeColor} name="theme-color" />
					<title>{metadata.title}</title>
				</Helmet>
				<LocalizationProvider>
					<UserProvider>
						<SentryIdentify />
						<ImpersonationProvider>
							<Analytics>
								<SegmentIdentify />
								<SegmentSession />
								<FullStory />
								<LogRocketProvider />
								<HubSpotChat />
								<BetterStackStatus />
								<SettingsProvider settings={settings.current}>
									<ThemeProvider>
										{children}
										<Toaster />
										<NotakuWidget />
										<ImpersonationBanner />
									</ThemeProvider>
								</SettingsProvider>
							</Analytics>
						</ImpersonationProvider>
					</UserProvider>
				</LocalizationProvider>
			</HelmetProvider>
		</Refine>
	)
}
